import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/app/AuthShell";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/auth/reset-password/verify", { params: { token } }).then(() => setState("valid")).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("La password deve contenere almeno 8 caratteri");
    if (pw !== pw2) return toast.error("Le due password non coincidono");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); setState("done"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (state === "checking") return <AuthShell title="Reimposta password"><div className="text-slate-500 text-sm">Verifica del link in corso…</div></AuthShell>;
  if (state === "invalid") return (
    <AuthShell title="Link non valido">
      <div className="space-y-4" data-testid="reset-invalid">
        <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700"><AlertTriangle className="w-5 h-5 shrink-0" /> Il link è scaduto o è già stato utilizzato. Richiedi un nuovo link di reimpostazione.</div>
        <Button asChild className="w-full bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="reset-request-new"><Link to="/password-dimenticata">Richiedi un nuovo link</Link></Button>
      </div>
    </AuthShell>
  );
  if (state === "done") return (
    <AuthShell title="Password aggiornata">
      <div className="space-y-4" data-testid="reset-done">
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800"><CheckCircle2 className="w-5 h-5 shrink-0" /> La nuova password è attiva. Ora puoi accedere al portale.</div>
        <Button asChild className="w-full bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="reset-go-login"><Link to="/login">Vai al login</Link></Button>
      </div>
    </AuthShell>
  );
  return (
    <AuthShell title="Reimposta password" subtitle="Scegli una nuova password di almeno 8 caratteri.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div><Label htmlFor="pw">Nuova password</Label><Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password-new" /></div>
        <div><Label htmlFor="pw2">Conferma password</Label><Input id="pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm" /></div>
        <Button type="submit" disabled={busy} className="w-full h-11 bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="reset-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva nuova password"}
        </Button>
      </form>
    </AuthShell>
  );
}
